import { graphql, useStaticQuery } from 'gatsby';
import Link from 'gatsby-link';
import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import { IconBook, IconCaret, IconClipboard, IconPhoneAid, IconSearch, H6 } from '@upsolve/ui';

import SCREENER_CTA_TYPES from '../analytics/screenerCtaTypes';
import getScreenerUrl from '../Links/getScreenerUrl';
import { withAmp } from '../AMP/AmpContext';
import Logo from '../../../static/images/upsolve-logo.svg';
import { A, ButtonLink } from '../analytics/LinksWithOnClick';
import trackComponentAction from '../analytics/trackComponentAction';
import UpsolveSearchBar from '../search/UpsolveSearchBar';

function getPath() {
  return typeof window === 'undefined' ? '' : window.location.pathname;
}

const headerLinkSections = [
  { value: 'bankruptcyBasics', label: 'Bankruptcy Basics' },
  { value: 'chapter7', label: 'Chapter 7 Bankruptcy' },
  { value: 'wageGarnishment', label: 'Wage Garnishment' },
  { value: 'propertyExemptions', label: 'Property & Exemptions' },
  { value: 'cars', label: 'Cars' },
];

const trackingProps = {
  componentName: 'Header',
  componentVersion: 2,
};

/**
 * Header
 * - v0: Navbar with heavy link list under learning center
 * - v1: Navbar with search and simpler cta: 'Join Upsolve'
 * - v2: Navbar with fixed mobile footer and fixed desktop header navbar w/ bigger CTA
 *
 * @component
 * @version 2
 */
const Header = (props) => {
  // let { showHelloBar = true } = props;
  const { contentGroup } = props;
  const screenerUrlNav = getScreenerUrl({ cta: SCREENER_CTA_TYPES.HEADER_NAV, contentGroup });
  const [isMenuShowing, setMenuShowing] = useState(false);
  const [isLearnPanelOpen, setLearnPanelOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(null);
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      links: allContentfulLearnArticle(filter: { siteLinking: { showInSiteNav: { eq: true} } }) {
        nodes {
          title
          slug
          siteLinking {
            showInSiteNav
          }
          tags {
            value
          }
        }
      }
    }
  `);

  // On Mount
  useEffect(() => {
    setIsMobile(window.innerWidth < 720);
  }, [])

  return (
    <div>
      {/* {showHelloBar !== false && <HelloBar {...props} />} */}

      <NavBar color={props.color}>
        <div className="nav__lead">
          <span
            role="button"
            aria-label="Toggle Menu"
          >
            <Link to="/"><Logo /></Link>
          </span>
        </div>

        <nav className={`nav__links ${isMenuShowing ? 'show' : ''}`}>
          <div>
            <Link aria-busy="false" to="/learn/how-to-file-bankruptcy/">
              How to File Bankruptcy
            </Link>
          </div>
          <div>
            <Link aria-busy="false" to="/learn/transparency/">
              How Upsolve is Free
            </Link>
          </div>
          <div
            onMouseEnter={() => setLearnPanelOpen(true)}
            onMouseLeave={() => setLearnPanelOpen(false)}
          >
            <Link aria-busy="false" to="/learn/">
              Learn About Bankruptcy <span className="caret"><IconCaret /></span>
            </Link>
              <div className={`nav__links__list ${isLearnPanelOpen ? 'show' : ''}`}>
                {headerLinkSections.map((headerLinkSection) => (
                  <div key={headerLinkSection.value}>
                    <H6>{headerLinkSection.label}</H6>
                    <ul key={headerLinkSections.values}>
                      {data.links.nodes
                        .filter(node => node.tags.some(tag => tag.value === headerLinkSection.value))
                        .map(node => (
                          <li key={node.slug}>
                            <Link
                              aria-busy="false"
                              to={`/learn/${node.slug}/`}
                              onClick={() => {
                                setLearnPanelOpen(false);
                                setMenuShowing(false);
                              }}
                            >
                              {node.title}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
          </div>
        </nav>

        <div className="nav__ctas">
          <span className="nav__ctas__search" onClick={(ev) => {
            if (!getPath().includes('/search')) setSearchOpen(true);
          }}>
            <IconSearch />
          </span>
          <ButtonLink
            aria-busy="false"
            className="hide-sm"
            href={screenerUrlNav}
            inverted
            onClick={() => trackComponentAction({ ...trackingProps, actionId: 'header.screenerNavigation', actionMethod: 'click' })}
          >
            <IconPhoneAid />
            Free Filing Tool
          </ButtonLink>
        </div>
      </NavBar>

      {isSearchOpen && (
        <UpsolveSearchBar
          autoFocus={true}
          onClose={() => setSearchOpen(false)}
          onSearch={() => setSearchOpen(false)}
          position="navbar"
        />
      )}

      {isMenuShowing && (
        <MobileMenuBackground onClick={() => setMenuShowing(false)} />
      )}

      {isMobile && (
        <MobileFooterNav>
          <Link to="/learn/" onClick={(ev) => {
            if (getPath().includes('/learn/')) {
              ev.preventDefault();
              setMenuShowing(!isMenuShowing);
            }
          }}>
            <div className={getPath().includes('/learn/') && !getPath().includes('/learn/how-to-file-bankruptcy/') ? 'active' : ''}>
              <IconBook />
              Free Articles
            </div>
          </Link>
          <Link to="/learn/how-to-file-bankruptcy/">
            <div  className={getPath().includes('/learn/how-to-file-bankruptcy/') ? 'active' : ''}>
              <IconClipboard />
              Filing Guide
            </div>
          </Link>
          <A
            href={getScreenerUrl({ cta: SCREENER_CTA_TYPES.HEADER_MOBILE_FOOTER, contentGroup })}
            onClick={() => trackComponentAction({ ...trackingProps, actionId: 'header.fixedFooter.screenerNavigation', actionMethod: 'click' })}
          >
            <div>
              <IconPhoneAid />
              Free Filing Tool
            </div>
          </A>
        </MobileFooterNav>
      )}

    </div>
  );
}

const NavBar = styled.header`
  background: ${props => props.theme.colors.white[900]};
  border-bottom: 1px solid ${props => props.theme.colors.white[500]};
  box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  width: 100%;
  z-index: 999;

  @media(min-width: ${props => props.theme.breakpoints[500]}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
  }

  a {
    color: ${props => props.theme.colors.brand[500]};
    &:hover {
      color: ${props => props.theme.colors.brand[100]};
    }
    font-size: 14px;
    button {
      font-weight: 700;
    }
  }

  .nav__lead, .nav__links, .nav__ctas {
    flex-grow: 1;
    height: 64px;
  }

  .nav__lead {
    max-width: 150px;
    display: inline-flex;
    align-items: center;
    margin-left: 1em;
    .icon-menu {
      width: 24px;
      max-width: 44px;
      display: none;
      @media(max-width: ${props => props.theme.breakpoints[500]}) {
        display: initial;
      }
    }
    svg {
      display: block;
      height: 32px;
      width: 140px;
      margin-top: -2px;
      @media(max-width: ${props => props.theme.breakpoints[500]}) {
        height: 28px;
        width: 120px;
        margin-top: 0;
      }
      path {
        fill: ${props => props.theme.colors.brand[500]};
      }
    }
  }
  .nav__links {
    .caret > svg {
      height: 8px;
      width: 8px;
      path {
        fill: ${props => props.theme.colors.brand[500]};
      }
    }
    // Desktop (Wide)
    & > .nav__links__header {
      display: none;
    }
    @media(min-width: ${props => props.theme.breakpoints[500]}) {
      display: flex;
      flex-grow: 1;
      justify-content: center;
      & > div {
        display: flex;
        align-items: center;
        & > a {
          padding: 0.75em;
          font-weight: 500;
        }
      }
    }
    // Mobile (Slim)
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      position: fixed;
      overflow-y: scroll;
      width: 100vw;
      height: 90vh;
      z-index: 998;
      transform: translateY(100vh);
      transition: 250ms;
      transition-timing-function: ease-in-out;
      background: ${props => props.theme.colors.white[900]};
      border-top: 4px solid ${props => props.theme.colors.white[500]};
      padding: 0 0 3em;
      &.show {
        transform: translateY(10vh);
      }
      & > .nav__links__header {
        display: flex;
        justify-content: space-between;
      }
      & > div {
        padding: 1em;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 2.5em;
        }
        &:not(:last-of-type) {
          border-bottom: 1px solid ${props => props.theme.colors.white[300]};
        }
        & > a {
          font-size: 18px;
          color: ${props => props.theme.colors.brand[500]};
        }
      }
    }
  }
  .nav__links__list {
    // Desktop (Wide)
    @media(min-width: ${props => props.theme.breakpoints[500]}) {
      display: none;
      width: 100vw;
      position: absolute;
      z-index: 998;
      left: 0;
      right: 0;
      top: 64px;
      background: ${props => props.theme.colors.white[800]};
      border-top: 1px solid ${props => props.theme.colors.white[500]};
      box-shadow: 0 5px 15px -5px rgba(0, 0, 25, 0.2);
      padding: 0.5em 2em;
      &.show {
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: repeat(5, 1fr);
      }
      a {
        font-size: 13px;
      }
      div {
        margin: 1em 0;
      }
      ul {
        padding: 0 0.5em 0 1em;
        list-style-type: disc;
        flex: 1;
      }
      li {
        margin-bottom: 0.2em;
        color: ${props => props.theme.colors.brand[500]};
      }
    }
    // Mobile (Slim)
    @media(max-width: ${props => props.theme.breakpoints[500]}) {
      div {
        margin: 1em 0;
      }
      ul {
        padding: 0;
        margin: 0;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 1em;
        }
      }
      li {
        margin-bottom: 0.65em;
        list-style-type: disc;
        color: ${props => props.theme.colors.brand[500]};
        margin-left: 1.25em;
        a {
          font-size: 15px;
        }
      }
    }
  }
  .nav__ctas {
    display: flex;
    flex-grow: 0;
    align-items: center;
    justify-content: flex-end;
    & > * {
      margin-right: 1.25em;
    }
    & > a {
      border-radius: 12px;
      color: white;
      padding-left: 16px;
      padding-right: 16px;
      color: ${props => props.theme.colors.brand[500]};
      font-weight: 500;
      svg {
        height: 27px;
        min-height: 27px;
        position: relative;
        top: -1px;
        margin-right: 4px;
      }
    }
    .nav__ctas__search {
      transition: 250ms;
      height: 36px;
      width: 36px;
      margin: 10px 8px;
      padding: 7px;
      display: flex;
      align-items: center;
      justify-content: center;
      path {
        fill: ${props => props.theme.colors.gray[500]};
      }
      &:hover {
        fill: ${props => props.theme.colors.brand[500]};
        cursor: pointer;
      }
    }
  }
`;

const MobileFooterNav = styled.div`
  display: none;
  position: fixed;
  z-index: 999;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px;
  max-height: 72px;
  background: white;
  border-top: 1px solid ${props => props.theme.colors.white[500]};
  @media(max-width: ${props => props.theme.breakpoints[500]}) {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.25em 2em 0;
  }
  & > a, div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: ${props => props.theme.colors.gray[500]};
    svg {
      height: 26px;
      path {
        fill: ${props => props.theme.colors.gray[500]};
      }
    }
    .active {
      color: ${props => props.theme.colors.brand[500]};
      svg path {
        fill: ${props => props.theme.colors.brand[500]};
      }
    }
  }
`;

const MobileMenuBackground = styled.div`
  z-index: 995;
  min-width: 100vw;
  width: 100vw;
  min-height: 100vh;
  height: 110vh;
  position: fixed;
  background: ${props => props.theme.colors.black[100]};
  opacity: 0.65;
  top: -20vh;
`;

export default withAmp(Header);
